import MarkdownIt from "markdown-it"; // Markdown转HTML解析器
import hljs from "highlight.js"; // 代码高亮库
import "highlight.js/styles/atom-one-dark.css"; // 代码高亮样式
import katex from "katex"; // 数学公式库
import mkatex from "markdown-it-katex"; // Markdown支持LaTeX公式
import "katex/dist/katex.min.css"; // 数学公式样式
import * as echarts from "echarts"; // ECharts图表库
import markdownItEcharts from "markdown-it-echarts"; // Markdown支持ECharts
import debounce from "lodash/debounce"; // 防抖函数，用于优化图表性能
import mermaid from "mermaid";

// 与 ChatPage 共用的 markdown-it 配置
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return `<div class="code-block">
                  <pre class="hljs"><code class="language-${lang}">${hljs.highlight(str, { language: lang, ignoreIllegals: true }).value
          }</code></pre>
                </div>`;
      } catch (__) {
        // 忽略错误，使用默认渲染
      }
    }
    return `<pre class="hljs"><code>${md.utils.escapeHtml(str)}</code></pre>`;
  },
});

md.use(markdownItEcharts, { echarts });
md.use(mkatex);

// 检查URL是否包含指定路径
function checkUrlContainsPath(url, targetPath) {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const pathSegments = pathname.split("/").filter((segment) => segment.trim() !== "");
    return pathSegments.includes(targetPath);
  } catch (err) {
    console.error("URL解析错误:", err);
    return false;
  }
}

// 初始化 Mermaid（仅客户端）
if (typeof window !== "undefined") {
  mermaid.initialize({
    startOnLoad: false,
    theme: "default",
    securityLevel: "loose",
    suppressErrorRendering: true,
  });
}

// 与 ChatPage.formatMessage 完全一致的 Markdown 渲染（含代码高亮、KaTeX、ECharts、链接规则等）
export function formatChatMessage(text) {
  if (!text) return "";

  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const hrefIndex = token.attrIndex("href");
    if (hrefIndex >= 0) {
      const href = token.attrs[hrefIndex][1];
      const hasTarget = checkUrlContainsPath(href, "ai_open_file");
      if (hasTarget) {
        const fileId = href.split("/").pop();
        const buttonHtml = `<span class="span-link" data-file-id="${fileId}" onclick="window.openExcelViewer('${fileId}')">${tokens[idx + 1].content}</span>`;

        token.attrSet("is-special-link", "true");

        if (tokens[idx + 1] && tokens[idx + 1].type === "text") {
          tokens[idx + 1].content = "";
        }

        return buttonHtml;
      } else {
        token.attrSet("target", "_blank");
        token.attrSet("rel", "noopener noreferrer");
      }
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.link_close = (tokens, idx, options, env, self) => {
    const linkOpenToken = tokens[idx - 2];
    if (linkOpenToken && linkOpenToken.attrGet("is-special-link") === "true") {
      return "";
    }
    return self.renderToken(tokens, idx, options);
  };

  const renderMath = (content, displayMode) => {
    try {
      return katex.renderToString(content, { displayMode });
    } catch (error) {
      return displayMode ? `$$${content}$$` : `$${content}$`;
    }
  };

  md.renderer.rules.math_inline = function (tokens, idx) {
    const content = tokens[idx].content;
    try {
      return katex.renderToString(content, { displayMode: false });
    } catch (error) {
      return `$${content}$`;
    }
  };

  md.renderer.rules.math_block = function (tokens, idx) {
    const content = tokens[idx].content;
    try {
      return katex.renderToString(content, { displayMode: true });
    } catch (error) {
      return `$$${content}$$`;
    }
  };

  text = text.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$");
  text = text.replace(/\\\(/g, "$").replace(/\\\)/g, "$");
  text = text
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, formula) => renderMath(formula, true))
    .replace(/\$([^$]*?)\$/g, (_, formula) => renderMath(formula, false));

  text = text.replace(/\n(\d+)/g, "\n $1 ");

  text = text.replace(/```echarts([\s\S]*?)```/g, (_, option) => {
    const containerId = `echarts-${Math.random().toString(36).substr(2, 9)}`;
    setTimeout(() => {
      const container = document.getElementById(containerId);
      console.log("是否获取到container", container);
      if (container) {
        const chart = echarts.init(container);

        const chartOption = JSON.parse(option);
        chart.setOption(chartOption);

        const debouncedResize = debounce(() => {
          if (chart && !chart.isDisposed()) {
            chart.resize();
          }
        }, 100);

        const resizeObserver = new ResizeObserver(() => {
          debouncedResize();
        });
        resizeObserver.observe(container);

        window.addEventListener("resize", debouncedResize);

        container._echartsCleanup = () => {
          resizeObserver.disconnect();
          window.removeEventListener("resize", debouncedResize);
          if (chart && !chart.isDisposed()) {
            chart.dispose();
          }
        };
      }
    });
    return `<div id="${containerId}" class="echarts-container"></div>`;
  });

  // Mermaid 图表渲染
  text = text.replace(/```mermaid([\s\S]*?)```/g, (_, code) => {
    const mermaidId = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
    setTimeout(async () => {
      const container = document.getElementById(mermaidId);
      if (!container) return;
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: "default",
          securityLevel: "loose",
          suppressErrorRendering: true,
        });
        const { svg } = await mermaid.render(`mermaid-svg-${mermaidId}`, code.trim());
        container.innerHTML = svg;
      } catch (err) {
        console.error("Mermaid渲染错误:", err);
      }
    }, 0);
    return `<div id="${mermaidId}" class="mermaid-container"></div>`;
  });

  return md.render(text);
}
